/**
 * General Appearance Examination Data Points
 * Category: General Appearance
 */

export const generalAppearanceDataPoints = [
  {
    id: 'overall_appearance',
    label: 'Overall Appearance',
    category: 'General Appearance',
    subcategory: 'General',
    fieldType: 'single',
    isActive: true,
    placeholder: 'Select overall appearance',
    options: [
      'Well-appearing',
      'Ill-appearing',
      'Acutely ill',
      'Chronically ill',
      'Well-developed',
      'Well-nourished',
      'Malnourished',
      'Cachectic'
    ],
    default: 'Well-appearing',
    ui: {
      span: 6,
      variant: 'select',
      display: 'dropdown'
    },
    code: '248153007', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'level_of_consciousness',
    label: 'Level of Consciousness',
    category: 'General Appearance',
    subcategory: 'Neurological',
    fieldType: 'single',
    isActive: true,
    placeholder: 'Select consciousness level',
    options: ['Alert and oriented', 'Alert but confused', 'Lethargic', 'Obtunded', 'Stuporous', 'Comatose'],
    default: 'Alert and oriented',
    ui: {
      span: 6,
      variant: 'select',
      display: 'dropdown'
    },
    code: '106167005', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'orientation',
    label: 'Orientation',
    category: 'General Appearance',
    subcategory: 'Neurological',
    fieldType: 'multi',
    isActive: true,
    placeholder: 'Select orientation',
    options: ['Oriented to person', 'Oriented to place', 'Oriented to time', 'Oriented to situation'],
    default: ['Oriented to person', 'Oriented to place', 'Oriented to time'],
    ui: {
      span: 6,
      variant: 'checkbox',
      display: 'chip'
    },
    code: '311465003', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'distress_level',
    label: 'Distress Level',
    category: 'General Appearance',
    subcategory: 'General',
    fieldType: 'single',
    isActive: true,
    placeholder: 'Select distress level',
    options: [
      'No acute distress',
      'Mild distress',
      'Moderate distress',
      'Severe distress',
      'Respiratory distress',
      'Pain distress'
    ],
    default: 'No acute distress',
    ui: {
      span: 6,
      variant: 'select',
      display: 'dropdown'
    },
    code: '271594007', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'body_habitus',
    label: 'Body Habitus',
    category: 'General Appearance',
    subcategory: 'Physical',
    fieldType: 'single',
    isActive: true,
    placeholder: 'Select body habitus',
    options: ['Normal build', 'Thin/Underweight', 'Overweight', 'Obese', 'Morbidly obese', 'Athletic build', 'Frail'],
    default: 'Normal build',
    ui: {
      span: 4,
      variant: 'select',
      display: 'dropdown'
    },
    code: '248160001', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'hygiene_grooming',
    label: 'Hygiene & Grooming',
    category: 'General Appearance',
    subcategory: 'General',
    fieldType: 'single',
    isActive: true,
    placeholder: 'Select hygiene status',
    options: ['Good hygiene', 'Fair hygiene', 'Poor hygiene', 'Well-groomed', 'Unkempt', 'Disheveled'],
    default: 'Good hygiene',
    ui: {
      span: 4,
      variant: 'select',
      display: 'dropdown'
    },
    code: '284551006', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'mobility_gait',
    label: 'Mobility & Gait',
    category: 'General Appearance',
    subcategory: 'Physical',
    fieldType: 'single',
    isActive: true,
    placeholder: 'Select mobility status',
    options: [
      'Ambulatory',
      'Ambulatory with assistance',
      'Uses walker',
      'Uses cane',
      'Wheelchair bound',
      'Bedbound',
      'Unsteady gait',
      'Normal gait'
    ],
    default: 'Ambulatory',
    ui: {
      span: 4,
      variant: 'select',
      display: 'dropdown'
    },
    code: '365980008', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'speech_communication',
    label: 'Speech & Communication',
    category: 'General Appearance',
    subcategory: 'Neurological',
    fieldType: 'multi',
    isActive: true,
    placeholder: 'Select speech characteristics',
    options: [
      'Clear speech',
      'Slurred speech',
      'Hoarse voice',
      'Aphasia',
      'Dysarthria',
      'Appropriate responses',
      'Delayed responses',
      'Confused responses'
    ],
    default: ['Clear speech', 'Appropriate responses'],
    ui: {
      span: 6,
      variant: 'checkbox',
      display: 'chip'
    },
    code: '87335007', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'mood_affect',
    label: 'Mood & Affect',
    category: 'General Appearance',
    subcategory: 'Psychiatric',
    fieldType: 'single',
    isActive: true,
    placeholder: 'Select mood/affect',
    options: [
      'Appropriate mood',
      'Anxious',
      'Depressed',
      'Euphoric',
      'Irritable',
      'Labile',
      'Flat affect',
      'Cooperative'
    ],
    default: 'Appropriate mood',
    ui: {
      span: 6,
      variant: 'select',
      display: 'dropdown'
    },
    code: '106126000', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'eye_contact',
    label: 'Eye Contact & Interaction',
    category: 'General Appearance',
    subcategory: 'Social',
    fieldType: 'single',
    isActive: true,
    placeholder: 'Select interaction quality',
    options: [
      'Good eye contact',
      'Poor eye contact',
      'Avoids eye contact',
      'Cooperative',
      'Uncooperative',
      'Withdrawn',
      'Engaging'
    ],
    default: 'Good eye contact',
    ui: {
      span: 6,
      variant: 'select',
      display: 'dropdown'
    },
    code: '225323000', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'positioning_posture',
    label: 'Positioning & Posture',
    category: 'General Appearance',
    subcategory: 'Physical',
    fieldType: 'single',
    isActive: true,
    placeholder: 'Select positioning',
    options: [
      'Sitting comfortably',
      'Lying comfortably',
      'Tripod position',
      'Orthopneic',
      'Restless',
      'Rigid posture',
      'Guarding',
      'Favoring one side'
    ],
    default: 'Sitting comfortably',
    ui: {
      span: 6,
      variant: 'select',
      display: 'dropdown'
    },
    code: '363855006', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'apparent_age',
    label: 'Apparent Age',
    category: 'General Appearance',
    subcategory: 'General',
    fieldType: 'single',
    isActive: true,
    placeholder: 'Select apparent age',
    options: ['Appears stated age', 'Appears younger than stated age', 'Appears older than stated age'],
    default: 'Appears stated age',
    ui: {
      span: 4,
      variant: 'select',
      display: 'dropdown'
    },
    code: '248153007', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
]

export default generalAppearanceDataPoints
